import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ShieldCheck, 
  IdentificationCard, 
  Camera, 
  User, 
  CheckCircle, 
  Warning,
  ArrowRight,
  ArrowLeft,
  Upload,
  Sparkle,
  FileText,
  Scales,
  Image as ImageIcon,
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 'info', title: 'Personal Info', icon: User, description: 'Your legal name and details' },
  { id: 'id_upload', title: 'Government ID', icon: IdentificationCard, description: 'Upload a photo of your ID' },
  { id: 'selfie', title: 'Selfie + ID', icon: Camera, description: 'Photo of you holding your ID' },
  { id: 'agreement', title: 'Agreement', icon: Scales, description: 'Sign the Creator Agreement' },
  { id: 'review', title: 'Submit', icon: ShieldCheck, description: 'Final review & submit' },
];

const VerificationPage = () => {
  const { user, refreshUser } = useAuth();
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [agreementStatus, setAgreementStatus] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    full_legal_name: '',
    date_of_birth: '',
    address: '',
    country: 'US',
    id_type: 'drivers_license',
  });

  // File uploads
  const [idFile, setIdFile] = useState(null);
  const [idPreview, setIdPreview] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const idInputRef = useRef(null);
  const selfieInputRef = useRef(null);

  // Agreement checkboxes
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [agreedContent, setAgreedContent] = useState(false);
  const [agreed2257, setAgreed2257] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const [verRes, agrRes] = await Promise.all([
        axios.get(`${API}/api/verification/status`, { withCredentials: true }),
        axios.get(`${API}/api/legal/creator-agreement/status`, { withCredentials: true }).catch(() => ({ data: { signed: false } })),
      ]);
      setVerificationStatus(verRes.data);
      setAgreementStatus(agrRes.data);
      
      const steps = verRes.data.steps_completed || [];
      if (agrRes.data.signed && steps.includes('selfie')) setCurrentStep(4);
      else if (steps.includes('selfie')) setCurrentStep(3);
      else if (steps.includes('id_upload')) setCurrentStep(2);
      else if (steps.includes('info')) setCurrentStep(1);
      
      if (verRes.data.full_legal_name) {
        setFormData(prev => ({ ...prev, full_legal_name: verRes.data.full_legal_name }));
      }
    } catch (err) {
      console.error('Failed to fetch verification status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleFileSelect = (file, type) => {
    if (!file) return;
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File too large (max 10MB)');
      return;
    }
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPEG, PNG, or WebP images allowed');
      return;
    }
    const preview = URL.createObjectURL(file);
    if (type === 'id') {
      setIdFile(file);
      setIdPreview(preview);
    } else {
      setSelfieFile(file);
      setSelfiePreview(preview);
    }
  };

  const startVerification = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/verification/start`, {}, { withCredentials: true });
      await fetchStatus();
      toast.success('Verification started');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start');
    } finally {
      setSubmitting(false);
    }
  };

  const submitInfo = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/verification/submit-step`, {
        step: 'info',
        data: formData,
      }, { withCredentials: true });
      await fetchStatus();
      setCurrentStep(1);
      toast.success('Personal info saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const uploadId = async () => {
    if (!idFile) { toast.error('Please select your ID photo'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', idFile);
      fd.append('id_type', formData.id_type);
      await axios.post(`${API}/api/verification/upload-id`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchStatus();
      setCurrentStep(2);
      toast.success('Government ID uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  const uploadSelfie = async () => {
    if (!selfieFile) { toast.error('Please take/select your selfie'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', selfieFile);
      await axios.post(`${API}/api/verification/upload-selfie`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchStatus();
      setCurrentStep(3);
      toast.success('Selfie uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  const signAgreement = async () => {
    if (!agreedTerms || !agreedContent || !agreed2257) {
      toast.error('You must agree to all policies');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/legal/creator-agreement/sign`, {
        full_legal_name: formData.full_legal_name || verificationStatus?.full_legal_name || '',
        agreed_to_terms: true,
        agreed_to_content_policy: true,
        agreed_to_2257: true,
      }, { withCredentials: true });
      await fetchStatus();
      setCurrentStep(4);
      toast.success('Creator Agreement signed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sign');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAutoVerify = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/verification/auto-verify`, {}, { withCredentials: true });
      await fetchStatus();
      await refreshUser();
      toast.success('Verification complete!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to verify');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (verificationStatus?.verification_status === 'verified') {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <div className="w-24 h-24 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
          <CheckCircle weight="fill" className="w-12 h-12 text-green-500" />
        </div>
        <h1 className="font-heading text-3xl font-bold text-white mb-4">You're Verified!</h1>
        <p className="text-slate-400 mb-2">Verified on {new Date(verificationStatus.verified_at).toLocaleDateString()}</p>
        <p className="text-slate-500 mb-6">You can now post content and receive payments.</p>

        {verificationStatus.performer_id && (
          <div className="max-w-md mx-auto mb-8 glass rounded-2xl p-5">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Your Performer ID</p>
            <div className="flex items-center justify-between gap-3">
              <code className="text-xl font-mono font-bold text-emerald-300" data-testid="performer-id-display">
                {verificationStatus.performer_id}
              </code>
              <button
                onClick={() => { navigator.clipboard?.writeText(verificationStatus.performer_id); toast.success('Copied'); }}
                className="px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 text-white rounded-lg"
                data-testid="copy-performer-id"
              >
                Copy
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-3 text-left">
              Share this ID with other creators for instant collab approval. They enter it when going live
              with you — no need to re-verify your ID every time.
            </p>
          </div>
        )}

        <div className="flex items-center justify-center gap-3 text-sm">
          <span className="flex items-center gap-2 px-4 py-2 bg-green-500/10 text-green-400 rounded-full">
            <CheckCircle weight="fill" className="w-4 h-4" /> ID Verified
          </span>
          <span className="flex items-center gap-2 px-4 py-2 bg-green-500/10 text-green-400 rounded-full">
            <CheckCircle weight="fill" className="w-4 h-4" /> Agreement Signed
          </span>
        </div>
      </div>
    );
  }

  if (verificationStatus?.verification_status === 'not_started') {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-3xl p-8 text-center">
          <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
            <ShieldCheck weight="duotone" className="w-10 h-10 text-amber-400" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-white mb-4">Creator Verification Required</h1>
          <p className="text-slate-400 mb-6 max-w-md mx-auto">
            To post content and receive payments, we need to verify your identity. This helps keep our platform safe and legally compliant.
          </p>
          
          <div className="grid grid-cols-5 gap-2 mb-8">
            {STEPS.map((step) => (
              <div key={step.id} className="text-center">
                <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-2">
                  <step.icon className="w-5 h-5 text-slate-400" />
                </div>
                <p className="text-[10px] text-slate-500">{step.title}</p>
              </div>
            ))}
          </div>
          
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6 text-left">
            <div className="flex items-start gap-3">
              <Warning weight="fill" className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-amber-200 font-medium mb-1">What you'll need:</p>
                <ul className="text-amber-200/70 space-y-1">
                  <li>Government-issued photo ID (driver's license, passport, or national ID)</li>
                  <li>A selfie of you holding your ID</li>
                  <li>Must be 18 years or older</li>
                </ul>
              </div>
            </div>
          </div>
          
          <button
            onClick={startVerification}
            disabled={submitting}
            className="px-8 py-4 bg-amber-500 text-amber-950 rounded-full font-semibold text-lg hover:bg-amber-600 transition-all disabled:opacity-50"
            data-testid="start-verification-btn"
          >
            {submitting ? 'Starting...' : 'Start Verification'}
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="font-heading text-2xl font-bold text-white mb-2">Creator Verification</h1>
        <p className="text-slate-400">Complete all steps to get verified</p>
      </div>

      {/* Progress */}
      <div className="flex items-center justify-between mb-8 relative">
        <div className="absolute top-5 left-0 right-0 h-0.5 bg-slate-800" />
        <div className="absolute top-5 left-0 h-0.5 bg-amber-500 transition-all" style={{ width: `${(currentStep / (STEPS.length - 1)) * 100}%` }} />
        {STEPS.map((step, i) => {
          const isCompleted = i < currentStep || (i === 3 && agreementStatus?.signed);
          const isCurrent = i === currentStep;
          return (
            <button
              key={step.id}
              onClick={() => i <= currentStep && setCurrentStep(i)}
              className={`relative z-10 flex flex-col items-center ${i <= currentStep ? 'cursor-pointer' : 'cursor-not-allowed'}`}
              disabled={i > currentStep}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                isCompleted ? 'bg-green-500 text-white' : isCurrent ? 'bg-amber-500 text-amber-950' : 'bg-slate-800 text-slate-500'
              }`}>
                {isCompleted ? <CheckCircle weight="fill" className="w-5 h-5" /> : <step.icon className="w-5 h-5" />}
              </div>
              <span className={`text-xs mt-2 hidden sm:block ${isCurrent ? 'text-amber-400' : 'text-slate-500'}`}>{step.title}</span>
            </button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={currentStep} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="glass rounded-2xl p-6">
          
          {/* Step 0: Personal Info */}
          {currentStep === 0 && (
            <div>
              <h2 className="text-xl font-semibold text-white mb-1">Personal Information</h2>
              <p className="text-slate-400 text-sm mb-6">Enter your legal name exactly as it appears on your government ID.</p>
              <div className="space-y-4">
                <div>
                  <Label>Full Legal Name *</Label>
                  <Input value={formData.full_legal_name} onChange={(e) => setFormData({...formData, full_legal_name: e.target.value})} placeholder="First Last" className="bg-slate-800 border-white/10 mt-1" data-testid="legal-name-input" />
                </div>
                <div>
                  <Label>Date of Birth *</Label>
                  <Input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})} max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]} className="bg-slate-800 border-white/10 mt-1" data-testid="dob-input" />
                </div>
                <div>
                  <Label>Address</Label>
                  <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="Street, City, State/Province" className="bg-slate-800 border-white/10 mt-1" />
                </div>
                <div>
                  <Label>Country</Label>
                  <select value={formData.country} onChange={(e) => setFormData({...formData, country: e.target.value})} className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-2 mt-1 text-white">
                    <option value="US">United States</option><option value="MX">Mexico</option><option value="CA">Canada</option><option value="GB">United Kingdom</option><option value="AU">Australia</option><option value="DE">Germany</option><option value="FR">France</option><option value="ES">Spain</option><option value="BR">Brazil</option><option value="OTHER">Other</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end mt-6">
                <button onClick={submitInfo} disabled={submitting || !formData.full_legal_name || !formData.date_of_birth} className="flex items-center gap-2 px-6 py-3 bg-amber-500 text-amber-950 rounded-full font-semibold hover:bg-amber-600 disabled:opacity-50" data-testid="submit-info-btn">
                  {submitting ? 'Saving...' : 'Continue'} <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 1: ID Upload (real file) */}
          {currentStep === 1 && (
            <div>
              <h2 className="text-xl font-semibold text-white mb-1">Government ID</h2>
              <p className="text-slate-400 text-sm mb-6">Upload a clear photo of your government-issued ID. All text must be readable.</p>
              <div className="space-y-4">
                <div>
                  <Label>ID Type</Label>
                  <select value={formData.id_type} onChange={(e) => setFormData({...formData, id_type: e.target.value})} className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-2 mt-1 text-white">
                    <option value="drivers_license">Driver's License</option>
                    <option value="passport">Passport</option>
                    <option value="id_card">National ID Card</option>
                  </select>
                </div>
                <div>
                  <input ref={idInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => handleFileSelect(e.target.files[0], 'id')} />
                  <button
                    onClick={() => idInputRef.current?.click()}
                    className="w-full border-2 border-dashed border-white/10 rounded-xl p-8 hover:border-amber-500/50 transition-colors text-center"
                    data-testid="id-upload-area"
                  >
                    {idPreview ? (
                      <img src={idPreview} alt="ID Preview" className="max-h-48 mx-auto rounded-lg" />
                    ) : (
                      <div>
                        <Upload className="w-10 h-10 text-slate-500 mx-auto mb-3" />
                        <p className="text-slate-400 font-medium">Click to upload your ID photo</p>
                        <p className="text-slate-600 text-xs mt-1">JPEG, PNG, or WebP — Max 10MB</p>
                      </div>
                    )}
                  </button>
                </div>
              </div>
              <div className="flex justify-between mt-6">
                <button onClick={() => setCurrentStep(0)} className="flex items-center gap-2 px-6 py-3 border border-white/10 text-white rounded-full hover:bg-white/5">
                  <ArrowLeft className="w-5 h-5" /> Back
                </button>
                <button onClick={uploadId} disabled={submitting || !idFile} className="flex items-center gap-2 px-6 py-3 bg-amber-500 text-amber-950 rounded-full font-semibold hover:bg-amber-600 disabled:opacity-50" data-testid="submit-id-btn">
                  {submitting ? 'Uploading...' : 'Upload & Continue'} <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Selfie */}
          {currentStep === 2 && (
            <div>
              <h2 className="text-xl font-semibold text-white mb-1">Selfie Verification</h2>
              <p className="text-slate-400 text-sm mb-6">Take a photo holding your ID next to your face.</p>
              <div className="bg-slate-800/50 rounded-xl p-4 mb-6">
                <h3 className="text-sm font-medium text-white mb-2">Tips:</h3>
                <ul className="text-sm text-slate-400 space-y-1">
                  <li>Face the camera with good lighting</li>
                  <li>Hold your ID at face level</li>
                  <li>Both your face and ID must be clearly visible</li>
                </ul>
              </div>
              <div>
                <input ref={selfieInputRef} type="file" accept="image/jpeg,image/png,image/webp" capture="user" className="hidden" onChange={(e) => handleFileSelect(e.target.files[0], 'selfie')} />
                <button
                  onClick={() => selfieInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-white/10 rounded-xl p-8 hover:border-amber-500/50 transition-colors text-center"
                  data-testid="selfie-upload-area"
                >
                  {selfiePreview ? (
                    <img src={selfiePreview} alt="Selfie Preview" className="max-h-48 mx-auto rounded-lg" />
                  ) : (
                    <div>
                      <Camera className="w-10 h-10 text-slate-500 mx-auto mb-3" />
                      <p className="text-slate-400 font-medium">Click to take/upload your selfie</p>
                      <p className="text-slate-600 text-xs mt-1">On mobile, this will open your camera</p>
                    </div>
                  )}
                </button>
              </div>
              <div className="flex justify-between mt-6">
                <button onClick={() => setCurrentStep(1)} className="flex items-center gap-2 px-6 py-3 border border-white/10 text-white rounded-full hover:bg-white/5">
                  <ArrowLeft className="w-5 h-5" /> Back
                </button>
                <button onClick={uploadSelfie} disabled={submitting || !selfieFile} className="flex items-center gap-2 px-6 py-3 bg-amber-500 text-amber-950 rounded-full font-semibold hover:bg-amber-600 disabled:opacity-50" data-testid="submit-selfie-btn">
                  {submitting ? 'Uploading...' : 'Upload & Continue'} <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Creator Agreement */}
          {currentStep === 3 && (
            <div>
              <h2 className="text-xl font-semibold text-white mb-1">Creator Agreement</h2>
              <p className="text-slate-400 text-sm mb-6">Please review and agree to the following policies before proceeding.</p>

              {agreementStatus?.signed ? (
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6">
                  <p className="text-green-300 text-sm flex items-center gap-2">
                    <CheckCircle weight="fill" className="w-5 h-5" />
                    Agreement signed on {new Date(agreementStatus.signed_at).toLocaleDateString()}
                  </p>
                </div>
              ) : (
                <div className="space-y-4 mb-6">
                  <label className="flex items-start gap-3 p-4 bg-slate-800/50 rounded-xl cursor-pointer hover:bg-slate-800/70 transition-colors">
                    <input type="checkbox" checked={agreedTerms} onChange={(e) => setAgreedTerms(e.target.checked)} className="mt-1 w-4 h-4 accent-blue-500" data-testid="agree-terms" />
                    <div>
                      <p className="text-white font-medium text-sm">Terms of Service & Creator Agreement</p>
                      <p className="text-slate-500 text-xs mt-1">I have read and agree to the <Link to="/legal/terms" target="_blank" className="text-blue-400 hover:underline">Terms of Service</Link> and <Link to="/legal/creator-agreement" target="_blank" className="text-blue-400 hover:underline">Creator Agreement</Link>, including the 90/10 revenue split (minimum tier) and content ownership terms.</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 p-4 bg-slate-800/50 rounded-xl cursor-pointer hover:bg-slate-800/70 transition-colors">
                    <input type="checkbox" checked={agreedContent} onChange={(e) => setAgreedContent(e.target.checked)} className="mt-1 w-4 h-4 accent-blue-500" data-testid="agree-content" />
                    <div>
                      <p className="text-white font-medium text-sm">Content Policy</p>
                      <p className="text-slate-500 text-xs mt-1">I have read the <Link to="/legal/content-policy" target="_blank" className="text-blue-400 hover:underline">Content Policy</Link> and agree not to upload prohibited content including non-consensual imagery, deepfakes, CSAM, or any content depicting minors.</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 p-4 bg-slate-800/50 rounded-xl cursor-pointer hover:bg-slate-800/70 transition-colors">
                    <input type="checkbox" checked={agreed2257} onChange={(e) => setAgreed2257(e.target.checked)} className="mt-1 w-4 h-4 accent-blue-500" data-testid="agree-2257" />
                    <div>
                      <p className="text-white font-medium text-sm">18 U.S.C. 2257 Compliance</p>
                      <p className="text-slate-500 text-xs mt-1">I certify that all persons depicted in my content are 18+ years old. I will maintain <Link to="/legal/2257" target="_blank" className="text-blue-400 hover:underline">2257-compliant records</Link> including government ID and consent documentation for all depicted persons.</p>
                    </div>
                  </label>
                </div>
              )}

              <div className="flex justify-between">
                <button onClick={() => setCurrentStep(2)} className="flex items-center gap-2 px-6 py-3 border border-white/10 text-white rounded-full hover:bg-white/5">
                  <ArrowLeft className="w-5 h-5" /> Back
                </button>
                {agreementStatus?.signed ? (
                  <button onClick={() => setCurrentStep(4)} className="flex items-center gap-2 px-6 py-3 bg-amber-500 text-amber-950 rounded-full font-semibold hover:bg-amber-600">
                    Continue <ArrowRight className="w-5 h-5" />
                  </button>
                ) : (
                  <button onClick={signAgreement} disabled={submitting || !agreedTerms || !agreedContent || !agreed2257} className="flex items-center gap-2 px-6 py-3 bg-amber-500 text-amber-950 rounded-full font-semibold hover:bg-amber-600 disabled:opacity-50" data-testid="sign-agreement-btn">
                    {submitting ? 'Signing...' : 'Sign Agreement'} <Scales className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Review & Submit */}
          {currentStep === 4 && (
            <div>
              <h2 className="text-xl font-semibold text-white mb-1">Review & Submit</h2>
              <p className="text-slate-400 text-sm mb-6">Everything looks good. Submit for verification.</p>
              <div className="space-y-3 mb-6">
                {[
                  { icon: User, label: 'Legal Name', value: formData.full_legal_name || verificationStatus?.full_legal_name, done: true },
                  { icon: IdentificationCard, label: 'Government ID', value: formData.id_type?.replace('_', ' '), done: verificationStatus?.is_id_verified },
                  { icon: Camera, label: 'Selfie + ID', value: 'Uploaded', done: verificationStatus?.is_selfie_verified },
                  { icon: Scales, label: 'Creator Agreement', value: 'Signed', done: agreementStatus?.signed },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <item.icon className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500">{item.label}</p>
                        <p className="text-white capitalize">{item.value || 'Not provided'}</p>
                      </div>
                    </div>
                    {item.done ? <CheckCircle weight="fill" className="w-5 h-5 text-green-500" /> : <Warning weight="fill" className="w-5 h-5 text-amber-400" />}
                  </div>
                ))}
              </div>
              <div className="flex justify-between">
                <button onClick={() => setCurrentStep(3)} className="flex items-center gap-2 px-6 py-3 border border-white/10 text-white rounded-full hover:bg-white/5">
                  <ArrowLeft className="w-5 h-5" /> Back
                </button>
                <button onClick={handleAutoVerify} disabled={submitting} className="flex items-center gap-2 px-8 py-3 bg-green-500 text-white rounded-full font-semibold hover:bg-green-600 disabled:opacity-50" data-testid="complete-verification-btn">
                  {submitting ? <><span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Verifying...</> : <><CheckCircle weight="fill" className="w-5 h-5" /> Complete Verification</>}
                </button>
              </div>
            </div>
          )}

        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default VerificationPage;
